import { useCallback, useState } from "react";
import { mockDataset } from "./mockData";
import type {
  CapabilityMapDataset,
  CapabilityMapFilters,
  CapabilityMapSelection,
  DomainMode,
  HeatmapMode,
  MarketStatus,
  NodeKind,
  PipelineConfig,
  ViewMode,
} from "./types";

export interface CapabilityMapState {
  dataset: CapabilityMapDataset;
  viewMode: ViewMode;
  domainMode: DomainMode;
  filters: CapabilityMapFilters;
  selection: CapabilityMapSelection;
  highlightedNodeIds: Set<string>;
  detailPanelOpen: boolean;
  marketStatusFilter: MarketStatus | null;
  searchQuery: string;
  selectedClusterId: string;
  pipelineConfig: PipelineConfig;
  heatmapMode: HeatmapMode;
  setViewMode: (mode: ViewMode) => void;
  setDomainMode: (mode: DomainMode) => void;
  setFilters: (filters: CapabilityMapFilters) => void;
  handleNodeClick: (nodeId: string, nodeKind: NodeKind) => void;
  closeDetailPanel: () => void;
  setMarketStatusFilter: (status: MarketStatus | null) => void;
  setSearchQuery: (query: string) => void;
  setSelectedClusterId: (id: string) => void;
  setPipelineConfig: (config: PipelineConfig) => void;
  setHeatmapMode: (mode: HeatmapMode) => void;
  resetFilters: () => void;
}

const DEFAULT_FILTERS: CapabilityMapFilters = {
  useCaseTags: [],
  domains: [],
  companyTypes: [],
  sourceTypes: [],
  systemTypeIds: [],
  componentTypeIds: [],
};

export function useCapabilityMapState(): CapabilityMapState {
  const [viewMode, setViewMode] = useState<ViewMode>("sunburst");
  const [domainMode, setDomainMode] = useState<DomainMode>("defense");
  const [filters, setFilters] = useState<CapabilityMapFilters>(DEFAULT_FILTERS);
  const [selection, setSelection] = useState<CapabilityMapSelection>({
    nodeId: null,
    nodeKind: null,
  });
  const [highlightedNodeIds, setHighlightedNodeIds] = useState<Set<string>>(
    new Set(),
  );
  const [detailPanelOpen, setDetailPanelOpen] = useState(false);
  const [marketStatusFilter, setMarketStatusFilter] =
    useState<MarketStatus | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedClusterId, setSelectedClusterId] = useState<string>(
    mockDataset.clusters[0].id,
  );
  const [pipelineConfig, setPipelineConfig] =
    useState<PipelineConfig>("demand_chain");
  const [heatmapMode, setHeatmapMode] = useState<HeatmapMode>("component");

  const handleNodeClick = useCallback((nodeId: string, nodeKind: NodeKind) => {
    setSelection({ nodeId, nodeKind });
    setDetailPanelOpen(true);

    const highlighted = new Set<string>();

    if (nodeKind === "company") {
      const company = mockDataset.companies.find((c) => c.id === nodeId);
      if (company) {
        company.capabilitiesCovered.forEach((subCapId) => {
          highlighted.add(subCapId);
          for (const cluster of mockDataset.clusters) {
            if (cluster.subCapabilities.some((sc) => sc.id === subCapId)) {
              highlighted.add(cluster.id);
            }
          }
        });
      }
    } else if (nodeKind === "sub_capability") {
      for (const company of mockDataset.companies) {
        if (company.capabilitiesCovered.includes(nodeId)) {
          highlighted.add(company.id);
        }
      }
    } else if (nodeKind === "capability_cluster") {
      const cluster = mockDataset.clusters.find((c) => c.id === nodeId);
      if (cluster) {
        for (const subCap of cluster.subCapabilities) {
          for (const company of mockDataset.companies) {
            if (company.capabilitiesCovered.includes(subCap.id)) {
              highlighted.add(company.id);
            }
          }
        }
      }
    }

    setHighlightedNodeIds(highlighted);
  }, []);

  const closeDetailPanel = useCallback(() => {
    setDetailPanelOpen(false);
    setSelection({ nodeId: null, nodeKind: null });
    setHighlightedNodeIds(new Set());
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setMarketStatusFilter(null);
    setSearchQuery("");
  }, []);

  return {
    dataset: mockDataset,
    viewMode,
    domainMode,
    filters,
    selection,
    highlightedNodeIds,
    detailPanelOpen,
    marketStatusFilter,
    searchQuery,
    selectedClusterId,
    pipelineConfig,
    heatmapMode,
    setViewMode,
    setDomainMode,
    setFilters,
    handleNodeClick,
    closeDetailPanel,
    setMarketStatusFilter,
    setSearchQuery,
    setSelectedClusterId,
    setPipelineConfig,
    setHeatmapMode,
    resetFilters,
  };
}
